import type { BootstrapConfig, SessionShell } from "../backend/backendApi";

export type BackendAuthPresentation = {
  authMode: string;
  authenticated: boolean;
  sessionLabel: string;
  sessionDescription: string;
  notice: string;
  signInHref: string | null;
  signInLabel: string | null;
};

type BuildBackendAuthPresentationInput = {
  status: "loading" | "ready" | "error";
  apiBaseUrl: string;
  bootstrapConfig?: BootstrapConfig | null;
  sessionShell?: SessionShell | null;
  errorMessage?: string | null;
};

export function buildBackendAuthPresentation(
  input: BuildBackendAuthPresentationInput
): BackendAuthPresentation {
  if (input.status === "loading") {
    return {
      authMode: "loading",
      authenticated: false,
      sessionLabel: "Loading session",
      sessionDescription: `Connecting to claw at ${input.apiBaseUrl}.`,
      notice: "Backend auth/session is loading.",
      signInHref: null,
      signInLabel: null
    };
  }

  if (input.status === "error") {
    return {
      authMode: input.sessionShell?.data.authMode ?? input.bootstrapConfig?.auth.mode ?? "unknown",
      authenticated: false,
      sessionLabel: "Backend unavailable",
      sessionDescription: input.errorMessage
        ? `Could not load backend session: ${input.errorMessage}`
        : "Could not load backend session from claw.",
      notice: "The shell is running in local preview fallback mode.",
      signInHref: null,
      signInLabel: null
    };
  }

  const authMode = input.sessionShell?.data.authMode ?? input.bootstrapConfig?.auth.mode ?? "unknown";
  const authenticated = Boolean(input.sessionShell?.data.authenticated);
  const currentUser = input.sessionShell?.data.currentUser;
  const providers = input.sessionShell?.data.providers ?? input.bootstrapConfig?.auth.providers ?? [];
  const signInHref = buildBackendAuthHref(input.apiBaseUrl, "/api/auth/google/redirect");

  return {
    authMode,
    authenticated,
    sessionLabel: authenticated
      ? currentUser?.displayName ?? currentUser?.email ?? "Signed-in user"
      : "Guest session",
    sessionDescription: authenticated
      ? currentUser?.email
        ? `Signed in as ${currentUser.email}.`
        : "Backend session is authenticated."
      : input.bootstrapConfig?.auth.gmailOnly
        ? "Google sign-in is expected for this claw deployment."
        : "Authentication is currently optional in this deployment.",
    notice: authenticated
      ? `Backend session is ready via ${input.apiBaseUrl}.`
      : "You are viewing a guest backend session.",
    signInHref: authenticated ? null : signInHref,
    signInLabel: authenticated
      ? null
      : providers.includes("google")
        ? "Sign in with Google"
        : "Sign in"
  };
}

function buildBackendAuthHref(baseUrl: string, path: string): string {
  const trimmedBaseUrl = baseUrl.replace(/\/+$/u, "");

  if (trimmedBaseUrl.startsWith("/")) {
    return `${trimmedBaseUrl}${path}`;
  }

  try {
    return new URL(path, `${trimmedBaseUrl}/`).toString();
  } catch {
    return path;
  }
}
